import { useEffect, useMemo, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { getRankingTable } from '../../services/ranking.service';
import { Box, IconButton, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RankingHistoryModal from './RankingHistoryModal';

const RankingTable = () => {
  // data and fetching state
  const [data, setData] = useState([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  // modal state
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedJugId, setSelectedJugId] = useState(null);
  const [selectedJugNombre, setSelectedJugNombre] = useState('');

  const handleCloseHistory = () => {
    setHistoryModalOpen(false);
    setSelectedJugId(null);
  };

  // table state
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 15,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!data.length) {
        setIsLoading(true);
      } else {
        setIsRefetching(true);
      }

      try {
        const result = await getRankingTable(pagination.pageIndex, pagination.pageSize, sorting, columnFilters);

        setData(result.data);
        setRowCount(result.totalRowCount);
      } catch (error) {
        setIsError(true);
        console.error(error);
        return;
      }
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columnFilters,
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
  ]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'apellido_nombre',
        header: 'Apellido y Nombre',
        size: 250
      },
      {
        accessorKey: 'jug_elo_actual',
        header: 'GRAU',
        size: 100,
        muiTableBodyCellProps: {
          align: 'center'
        },
        muiTableHeadCellProps: {
          align: 'center'
        }
      },
      {
        accessorKey: 'categoria',
        header: 'Categoría',
        size: 100
      }
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    enableRowSelection: false,
    enableRowActions: true,
    enableColumnActions: false,
    enableGlobalFilter: false,
    enableHiding: false,
    enableFilters: true,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    getRowId: (row) => row.jug_id,
    initialState: { showColumnFilters: true, density: 'compact' },
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    muiToolbarAlertBannerProps: isError
      ? {
        color: 'error',
        children: 'Error cargando datos',
      }
      : undefined,
    onColumnFiltersChange: (updater) => {
      setColumnFilters(updater);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
    muiTableHeadCellProps: {
      sx: {
        background: "#283f50",
        color: "#cacbd1"
      }
    },

    muiTableBodyCellProps: {
      sx: {
        background: '#20232f',
        color: '#ffffffb3',
      },
    },
    renderRowActions: ({ row }) => (
      <Box>
        <Tooltip title="Ver Gráfica">
          <IconButton
            style={{ color: '#1abbed' }}
            onClick={() => {
              setSelectedJugId(row.original.jug_id);
              setSelectedJugNombre(row.original.apellido_nombre);
              setHistoryModalOpen(true);
            }}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    positionActionsColumn: 'last',
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: 'Acciones',
      },
    },
  });

  return (
    <>
      <MaterialReactTable table={table} />
      <RankingHistoryModal
        open={historyModalOpen}
        handleClose={handleCloseHistory}
        jugId={selectedJugId}
        jugNombre={selectedJugNombre}
      />
    </>
  );
};

export default RankingTable;
